<template>
    <card-tabs v-if="contact" selected-name="contact">
        <template #labels>
            <card-tab-label name="contact">
                Contactpersoon
            </card-tab-label>

            <card-tab-label v-if="hasSecondaryContact" name="existing-partner">
                {{ contact.secondaryContact?.name.fullName || 'Partner' }}
            </card-tab-label>

            <button-text
                v-if="!hasSecondaryContact && props.editable"
                style="align-self: center;"
                @click="openPartnerEdit"
            >
                <svg-icon :icon="IconAdd" />
                <span>Partner toevoegen</span>
            </button-text>
        </template>

        <template #panels>
            <contact-card-panel
                :contact="contact as Contact"
                name="contact"
                variant="card"
                with-variable-height
                :editable="props.editable"
            />
            <contact-card-panel
                v-if="hasSecondaryContact && contact.secondaryContact"
                :contact="contact.secondaryContact as Contact"
                name="existing-partner"
                variant="card"
                with-variable-height
                :editable="props.editable"
            />
        </template>
    </card-tabs>
</template>

<script lang="ts" setup>
    import IconAdd from '@keyvue/one-material-icons/icons/add.svg?component';
    import { ButtonText, CardTabLabel, CardTabs, SvgIcon } from '@keyvue/one-ui';
    import { useRouter, useRoute, type RouteLocationRaw } from 'vue-router';
    import { Contact, ContactId } from '@keyvue/one-api-client';
    import { computed, ref } from 'vue';
    import useClient from '@composables/useClient';
    import ContactCardPanel from '@components/Tabs/Panels/ContactCardPanel.vue';
    import useEventBus from '@composables/useEventBus';

    export interface Props {
        contactId: ContactId
        editable?: boolean,
    }

    const props = defineProps<Props>();

    const eventBus = useEventBus();
    const router = useRouter();
    const route = useRoute();
    const { client } = useClient();

    const contact = ref<Contact>(await client.get(Contact, props.contactId));
    const hasSecondaryContact = computed(() => !!contact.value?.secondaryContact);

    const routeToContactEdit = computed(() => {
        const contactEditRoute: RouteLocationRaw = {
            name: 'contactEdit',
            params: {
                contactId: contact.value?.id.toString(),
            },
        };

        if ('leadId' in route.params) {
            contactEditRoute.name = 'leadContactEdit';
            contactEditRoute.params = {
                ...contactEditRoute.params,
                leadId: route.params.leadId.toString(),
            };
        }

        if ('orderId' in route.params) {
            contactEditRoute.name = 'orderContactEdit';
            contactEditRoute.params = {
                ...contactEditRoute.params,
                orderId: route.params.orderId.toString(),
            };
        }

        return contactEditRoute;
    });

    const openPartnerEdit = () => {
        router.push({
            ...routeToContactEdit.value,
            query: {
                contact: 'partner',
            },
        });
    };

    const loadContact = async () => {
        contact.value = await client.get(Contact, props.contactId);
    };

    eventBus.subscribe<string>('resource-manipulated', (params) => {
        if (params === 'Contact') {
            Promise.resolve(loadContact());
        }
    });
</script>
